import { twMerge } from '@sortlist-frontend/design-system';
import { useEffect, useRef, useState, type ReactNode } from 'react';

interface RevealOnScrollProps {
  as?: 'div' | 'li' | 'section';
  className?: string;
  children: ReactNode;
}

export const RevealOnScroll = (props: RevealOnScrollProps) => {
  const { as: Tag = 'div', className, children } = props;
  const ref = useRef<HTMLDivElement & HTMLLIElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (element == null) return;
    const prefersReducedMotion =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (typeof IntersectionObserver === 'undefined' || prefersReducedMotion) {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={twMerge(
        'transition-all duration-700 ease-out motion-reduce:transition-none',
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0',
        className,
      )}>
      {children}
    </Tag>
  );
};
